import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import workerProfileService from '../../services/workerProfileService';
import serviceAreaContentService from '../../services/serviceAreaContentService';
import { useAuth } from '../../contexts/AuthContext';

const JobSectorBrowserRoadConstructionWorkers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');
  const [certifiedWorkers, setCertifiedWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(true);
  const [news, setNews] = useState([]);
  const [areaContent, setAreaContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    loadWorkerProfiles();
    loadNewsAndContent();
  }, []);

  const loadWorkerProfiles = async () => {
    try {
      setLoadingWorkers(true);
      const data = await workerProfileService?.getWorkerProfilesByServiceArea('Stratenmakers & Grondwerkers');
      setCertifiedWorkers(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoadingWorkers(false);
    }
  };

  const loadNewsAndContent = async () => {
    try {
      setLoadingContent(true);
      const [newsData, contentData] = await Promise.all([
        serviceAreaContentService?.getNewsByServiceArea('Stratenmakers & Grondwerkers'),
        serviceAreaContentService?.getContentByServiceArea('Stratenmakers & Grondwerkers')
      ]);
      setNews(newsData || []);
      setAreaContent(contentData || []);
    } catch (error) {
      console.error('Error loading news and content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleContactClick = (worker) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: window.location?.pathname } });
      return;
    }
    // Handle contact logic here (e.g., open modal, send message)
    console.log('Contact worker:', worker);
  };

  const roadConstructionJobs = [
    {
      title: 'Wegenbouw project A2 corridor',
      location: 'Utrecht',
      budget: '€45.000 - €65.000',
      deadline: '12 weken',
      description: 'Asfaltering en fundering 2,5 km snelweg met drainage systemen',
      projectType: 'Wegenbouw',
      materials: 'Asfalt, beton, grind',
      crewSize: '8-12 personen',
      equipment: 'Asfalteermachine, walsen, graafmachines',
      certification: 'VCA, Wegenbouw certificaat',
      dailyRate: '€180',
      weatherDependent: true,
      complexity: 'high'
    },
    {
      title: 'Stoepvernieuwing winkelstraat',
      location: 'Amsterdam',
      budget: '€12.000 - €18.000',
      deadline: '4 weken',
      description: 'Vervanging betonnen stoeptegels en herinrichting voetgangersgebied',
      projectType: 'Stoepwerk',
      materials: 'Betonnen tegels, zand, cement',
      crewSize: '4-6 personen',
      equipment: 'Tegelzaag, trilplaat, handgereedschap',
      certification: 'VCA',
      dailyRate: '€165',
      weatherDependent: true,
      complexity: 'medium'
    },
    {
      title: 'Grondwerk nieuwbouwwijk',
      location: 'Rotterdam',
      budget: '€85.000 - €120.000',
      deadline: '16 weken',
      description: 'Grondverzet, fundering en drainage voor 45 woningen nieuwbouwproject',
      projectType: 'Grondwerk',
      materials: 'Zand, grind, drainage buizen',
      crewSize: '10-15 personen',
      equipment: 'Graafmachines, dumpers, bulldozers',
      certification: 'VCA, Machinist certificaat',
      dailyRate: '€175',
      weatherDependent: true,
      complexity: 'high'
    },
    {
      title: 'Parkeerterrein aanleg bedrijventerrein',
      location: 'Eindhoven',
      budget: '€28.000 - €38.000',
      deadline: '6 weken',
      description: 'Aanleg geparkeerd parkeerterrein met drainage en markering',
      projectType: 'Parkeerterrein',
      materials: 'Asfalt, betonrand, markering',
      crewSize: '6-8 personen',
      equipment: 'Asfalteermachine, walsen, markeringsmachine',
      certification: 'VCA',
      dailyRate: '€170',
      weatherDependent: true,
      complexity: 'medium'
    },
    {
      title: 'Rioolvervanging woonwijk',
      location: 'Den Haag',
      budget: '€35.000 - €50.000',
      deadline: '10 weken',
      description: 'Vervanging rioolsysteem met grondwerk en wegdek herstel',
      projectType: 'Riolering',
      materials: 'Rioolbuizen, zand, asfalt',
      crewSize: '8-10 personen',
      equipment: 'Graafmachines, rioolcamera, trilplaat',
      certification: 'VCA, Riolering specialist',
      dailyRate: '€185',
      weatherDependent: true,
      complexity: 'high'
    },
    {
      title: 'Fietspad aanleg recreatiegebied',
      location: 'Groningen',
      budget: '€15.000 - €22.000',
      deadline: '5 weken',
      description: 'Aanleg asfalt fietspad 1,2 km met fundering en belijning',
      projectType: 'Fietspad',
      materials: 'Asfalt, grind, markering',
      crewSize: '4-6 personen',
      equipment: 'Kleine asfalteermachine, walsen',
      certification: 'VCA',
      dailyRate: '€160',
      weatherDependent: true,
      complexity: 'low'
    },
    {
      title: 'Fundering industriële hal',
      location: 'Tilburg',
      budget: '€55.000 - €75.000',
      deadline: '8 weken',
      description: 'Grondwerk en betonnen fundering voor industriële hal 2000m²',
      projectType: 'Fundering',
      materials: 'Beton, wapening, zand',
      crewSize: '12-16 personen',
      equipment: 'Betonmixer, trilnaald, graafmachines',
      certification: 'VCA, Betonspecialist',
      dailyRate: '€190',
      weatherDependent: true,
      complexity: 'high'
    },
    {
      title: 'Landschapsarchitectuur park',
      location: 'Arnhem',
      budget: '€20.000 - €30.000',
      deadline: '7 weken',
      description: 'Grondmodellering, paden en drainage voor stadspark renovatie',
      projectType: 'Landschapswerk',
      materials: 'Grond, grind, drainage',
      crewSize: '6-8 personen',
      equipment: 'Graafmachines, walsen, handgereedschap',
      certification: 'VCA',
      dailyRate: '€165',
      weatherDependent: true,
      complexity: 'medium'
    }
  ];

  const specializations = [
    { value: 'all', label: 'Alle specialisaties' },
    { value: 'road-construction', label: 'Wegenbouw' },
    { value: 'paving', label: 'Bestrating & Stoepwerk' },
    { value: 'groundwork', label: 'Grondwerk & Fundering' },
    { value: 'drainage', label: 'Riolering & Drainage' },
    { value: 'concrete', label: 'Betonwerk' },
    { value: 'landscaping', label: 'Landschapswerk' }
  ];

  const getComplexityColor = (complexity) => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[complexity] || colors?.low;
  };

  const getComplexityLabel = (complexity) => {
    const labels = {
      high: 'Complex',
      medium: 'Gemiddeld',
      low: 'Basis'
    };
    return labels?.[complexity] || 'Basis';
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <motion.section 
          className="bg-gradient-to-br from-slate-50 to-gray-50 py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Icon name="Hammer" size={32} className="text-slate-600" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  Wegenbouw & Grondwerk - Vakgebied Browser
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Gespecialiseerde infrastructuurprojecten voor stratenmakers en grondwerkers met focus op wegenbouw, bestrating, fundering en civiele techniek.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve projecten', value: '67', icon: 'Briefcase' },
                { label: 'Gecertificeerde ploegen', value: '142', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.8', icon: 'Star' },
                { label: 'Voltooide projecten', value: '3.2k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6" style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4">Geavanceerde Filters</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Specialisatie</label>
                  <select 
                    value={selectedSpecialization}
                    onChange={(e) => setSelectedSpecialization(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {specializations?.map(spec => (
                      <option key={spec?.value} value={spec?.value}>{spec?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Locatie Radius</label>
                  <select 
                    value={locationRadius}
                    onChange={(e) => setLocationRadius(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                    <option value="nationwide">Landelijk</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Certificering</label>
                  <select className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="all">Alle certificeringen</option>
                    <option value="vca">VCA</option>
                    <option value="road-construction">Wegenbouw certificaat</option>
                    <option value="machinery">Machinist certificaat</option>
                    <option value="drainage">Riolering specialist</option>
                    <option value="concrete">Betonspecialist</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-heading font-bold text-foreground">Beschikbare Infrastructuurprojecten</h2>
              <div className="flex gap-2">
                <Button
                  variant={activeFilter === 'alle' ? 'primary' : 'outline'}
                  onClick={() => setActiveFilter('alle')}
                >
                  Alle ({roadConstructionJobs?.length})
                </Button>
                <Button
                  variant={activeFilter === 'urgent' ? 'primary' : 'outline'}
                  onClick={() => setActiveFilter('urgent')}
                >
                  Urgent
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {roadConstructionJobs?.map((job, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-primary transition-all duration-300"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-lg text-foreground">{job?.title}</h3>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getComplexityColor(job?.complexity)}`}>
                      {getComplexityLabel(job?.complexity)}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="MapPin" size={16} />
                      <span>{job?.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>Deadline: {job?.deadline}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Users" size={16} />
                      <span>Ploeg: {job?.crewSize}</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{job?.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="font-medium text-foreground">{job?.projectType}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Materialen:</span>
                      <span className="font-medium text-foreground text-right">{job?.materials}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Dagprijs:</span>
                      <span className="font-semibold text-primary">{job?.dailyRate}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <div className="text-xs text-muted-foreground mb-1">Vereiste certificering</div>
                    <div className="text-sm font-medium text-foreground">{job?.certification}</div>
                  </div>

                  {job?.weatherDependent && (
                    <div className="flex items-center gap-2 text-xs text-warning mb-4">
                      <Icon name="CloudRain" size={14} />
                      <span>Weerafhankelijk project</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button variant="primary" className="flex-1">
                      Project Solliciteren
                    </Button>
                    <Button variant="outline">
                      <Icon name="Info" size={16} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certified Workers Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-8">Gecertificeerde Infrastructuur Ploegen</h2>
            
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde Stratenmakers & Grondwerkers</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{certifiedWorkers?.length} professionals beschikbaar</span>
              </div>
            </div>

            {loadingWorkers ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : certifiedWorkers?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Geen professionals beschikbaar</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {certifiedWorkers?.map((worker, index) => (
                  <motion.div
                    key={worker?.id || index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{worker?.name}</h3>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1">
                            <Icon name="Star" size={14} className="text-warning fill-warning" />
                            <span className="text-sm font-medium text-foreground">{worker?.rating}</span>
                            <span className="text-xs text-muted-foreground">({worker?.reviews} reviews)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="MapPin" size={14} />
                          <span>{worker?.location}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                      <div className="text-sm text-primary font-medium">{worker?.specialization}</div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                      <div className="flex flex-wrap gap-2">
                        {worker?.certifications?.map((cert, idx) => (
                          <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Garantie</div>
                      <div className="text-sm text-muted-foreground">{worker?.warranty}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                        <div className="text-sm font-medium text-foreground">{worker?.experience}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                        <div className="text-sm font-medium text-foreground">{worker?.completed_projects}+</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                        <div className="text-xl font-bold text-foreground">{worker?.hourly_rate}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                          <div className="text-sm font-medium text-success">{worker?.availability}</div>
                        </div>
                        <Button size="sm" variant="outline" className="ml-2">
                          <Icon name="Phone" size={14} />
                          Contacteren
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Area Content Section */}
        {!loadingContent && areaContent?.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-8">Waarom kiezen voor onze stratenmakers & grondwerkers?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {areaContent?.map((content, index) => (
                  <motion.div
                    key={content?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{content?.title}</h3>
                        <p className="text-sm text-muted-foreground">{content?.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News Section */}
        {!loadingContent && news?.length > 0 && (
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Laatste Nieuws</h2>
                <Icon name="Newspaper" size={24} className="text-primary" />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {news?.map((newsItem, index) => (
                  <motion.div
                    key={newsItem?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{newsItem?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{newsItem?.content}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{newsItem?.author}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Klaar om te starten met uw infrastructuurproject?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Vind de juiste gecertificeerde ploeg voor uw wegenbouw, bestrating of grondwerk project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="primary" size="lg">
                  <Icon name="Plus" size={20} className="mr-2" />
                  Project Plaatsen
                </Button>
                <Button variant="outline" size="lg">
                  <Icon name="Users" size={20} className="mr-2" />
                  Ploeg Worden
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserRoadConstructionWorkers;